import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ISubscribePage } from 'pages/subscribe/subscribe-interface';
import { CsPage, NavController } from 'providers/nav-controller';
import { PromoCodeService } from 'services/promo-code.service';
import { PromoPageParams } from './promo-interface';

/**
 * The page a sale link opens (#6965): `https://mh-physique.app/promo?code=BLACKFRIDAY`.
 *
 * It keeps the code and moves straight on to the Subscribe page, which sends the
 * code with the checkout request and shows the sale. It exists as a page of its own,
 * rather than the link pointing at `/subscribe?code=`, so the link can be sent to the
 * browser on phones with the app installed: both the iOS association file and
 * `decideDeepLinkAction` exclude `/promo`, and excluding `/subscribe` would break
 * the app's own links to its Subscribe page.
 *
 * It must render for a signed-out visitor, so its name is in the signed-out list in
 * `AppComponent.pageFilterAsync` as well as in `app-page-list.ts` — registering it
 * alone sends the visitor to the login screen with nothing reported.
 * See design-docs/promo-campaigns.md.
 */
@Component({
  selector: 'page-promo',
  templateUrl: 'promo.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoPage extends CsPage<PromoPageParams> {
  constructor(
    private navCtrl: NavController,
    private promoCode: PromoCodeService,
  ) {
    super();
  }

  async ionViewWillEnter(): Promise<void> {
    // keep the code FIRST: the address bar loses its query string as soon as
    // navigation settles, and signing up reloads the app
    this.promoCode.captureFromUrl();
    // setRoot rather than push: the visitor arrived from a link, so there is
    // nothing behind this page to go back to
    await this.navCtrl.setRootAsync(ISubscribePage, {});
  }
}
